import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import applyCaseMiddleware from 'axios-case-converter'
// import { instrumentAxiosInstance } from '../monitoring'
import { CTW_API_ENDPOINT, LOCAL_ACCESS_TOKEN_KEY } from '../utils'

type RequestConfigWithAuthHandling = AxiosRequestConfig & {
    skipUnauthorizedHandling?: boolean;
};

const axios = applyCaseMiddleware(
    Axios.create({
        withCredentials: true, // Enable cookie support for future cookie-based auth
    })
)

const resetViewPiiPreference = async () => {
    await axios.request({
        baseURL: CTW_API_ENDPOINT,
        method: 'post',
        url: 'v2/admin/update-view-pii',
        data: { view_pii: false },
        skipUnauthorizedHandling: true,
    } as RequestConfigWithAuthHandling);
};

// instrumentAxiosInstance(axios)

axios.interceptors.request.use(
    config => {
        // Reverting to localStorage based auth
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config
    },
    error => {
        return Promise.reject(error)
    }
)

axios.interceptors.response.use(
    (response) => {
        // Cookie-based auth: Browser handles Set-Cookie automatically
        return response;
    },
    async (error) => {
        // Handle unauthorized/forbidden responses
        if (error.response?.status === 401 || error.response?.status === 403) {
            if (typeof window !== 'undefined') {
                const requestConfig = error.config as RequestConfigWithAuthHandling | undefined;
                if (!requestConfig?.skipUnauthorizedHandling) {
                    try {
                        await resetViewPiiPreference();
                    } catch (resetError) {
                        console.error('Unable to reset personal data masking:', resetError);
                    }
                }

                // Clear any residual localStorage data if needed, but primary auth is cookie
                window.localStorage.removeItem("user");
                window.localStorage.removeItem("accessToken");
                // Redirect to logout to clear cookies via backend
                window.location.href = '/admanager/v2/logout';
            }
        }
        return Promise.reject(error);
    }
);


const responseData = <T extends AxiosResponse<any>>(response: T) => response.data

const handleError = (error: AxiosError) => {
    throw error;
}

class CoreAPIService {
    resetViewPiiPreference = resetViewPiiPreference

    get = async <R>(baseURL: string, url: string, params: AnyObject = {}) =>
        axios
            .request<R>({
                baseURL,
                method: 'get',
                url,
                params,
            })
            .then<R>(responseData)
            .catch(handleError)

    post = async <R>(baseURL: string, url: string, data: AnyObject = {}, { ...config }: AxiosRequestConfig = {}) =>
        axios
            .request<R>({
                baseURL,
                method: 'post',
                url,
                data,
                ...config,
            })
            .then<R>(responseData)
            .catch(handleError)

    put = async <R>(baseURL: string, url: string, data: AnyObject) =>
        axios
            .request<R>({
                baseURL,
                method: 'put',
                url,
                data,
            })
            .then<R>(responseData)
            .catch(handleError)

    patch = async <R>(baseURL: string, url: string, data: AnyObject = {}) =>
        axios
            .request<R>({
                baseURL,
                method: 'patch',
                url: `${url}`,
                data,
            })
            .then<R>(responseData)
            .catch(handleError)

    delete = async <R>(baseURL: string, url: string, data: AnyObject = {}) =>
        axios
            .request<R>({
                baseURL,
                method: 'delete',
                url: `${url}`,
                data,
            })
            .then<R>(responseData)
            .catch(handleError)
}

// eslint-disable-next-line 
export default new CoreAPIService()
